import React from 'react'
import { Edit, useForm, useSelect } from '@refinedev/antd'
import { Form, Input, DatePicker, Checkbox, Divider, Select } from 'antd'
import type { Tables } from '@muaa/shared'
import dayjs from 'dayjs'
import { ImageUploadField } from '../../components/ImageUploadField'

type Event = Tables<'events'>
type EventSession = Tables<'event_sessions'>

export function EventEdit() {
  const { formProps, saveButtonProps, form, query } = useForm<Event>()
  const currentImageUrl: string | null = (query?.data?.data?.image_url as string | null) ?? null
  const editedEventId: string | undefined = (query?.data?.data?.id as string | undefined) ?? undefined
  // Featured picker scoped to THIS event's own sub-events — the DB constraint
  // (events_featured_session_same_event) rejects cross-event links, so the
  // picker only offers legal options.
  const { selectProps: sessionSelectProps } = useSelect<EventSession>({
    resource: 'event_sessions',
    optionLabel: 'title',
    optionValue: 'id',
    pagination: { mode: 'off' },
    filters: editedEventId ? [{ field: 'event_id', operator: 'eq', value: editedEventId }] : [],
    queryOptions: { enabled: Boolean(editedEventId) },
  })

  // Convert ISO date strings to dayjs objects for DatePicker, and back on submit.
  const transformedFormProps = {
    ...formProps,
    initialValues: formProps.initialValues
      ? {
          ...formProps.initialValues,
          start_date: formProps.initialValues.start_date
            ? dayjs(formProps.initialValues.start_date as string)
            : undefined,
          end_date: formProps.initialValues.end_date
            ? dayjs(formProps.initialValues.end_date as string)
            : undefined,
        }
      : undefined,
    onFinish: async (values: Record<string, unknown>) => {
      const out = { ...values }
      if (out.start_date && dayjs.isDayjs(out.start_date)) {
        out.start_date = (out.start_date as ReturnType<typeof dayjs>).format('YYYY-MM-DD')
      }
      if (out.end_date && dayjs.isDayjs(out.end_date)) {
        out.end_date = (out.end_date as ReturnType<typeof dayjs>).format('YYYY-MM-DD')
      }
      return formProps.onFinish?.(out as Parameters<typeof formProps.onFinish>[0])
    },
  }

  return (
    <Edit saveButtonProps={saveButtonProps}>
      <Form {...transformedFormProps} layout="vertical">
        <Form.Item label="Title" name="title" rules={[{ required: true }]}>
          <Input />
        </Form.Item>
        <Form.Item label="Subtitle" name="subtitle">
          <Input />
        </Form.Item>
        <Form.Item
          label="Start Date"
          name="start_date"
          normalize={(value) =>
            value ? (dayjs.isDayjs(value) ? value.format('YYYY-MM-DD') : value) : null
          }
        >
          <DatePicker style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item
          label="End Date"
          name="end_date"
          normalize={(value) =>
            value ? (dayjs.isDayjs(value) ? value.format('YYYY-MM-DD') : value) : null
          }
        >
          <DatePicker style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item label="Location" name="location">
          <Input />
        </Form.Item>
        <Form.Item label="Description" name="description">
          <Input.TextArea rows={4} />
        </Form.Item>

        <Divider orientation="left">Tickets &amp; Payments</Divider>
        <Form.Item
          label="Cheddar Up Ticket URL"
          name="cheddarup_url"
          tooltip="Cheddar Up page for paid tickets; leave blank to hide the Get Tickets button in the app."
        >
          <Input placeholder="https://muaa.cheddarup.com/c/..." />
        </Form.Item>

        <Divider orientation="left">Home Hero</Divider>
        <Form.Item
          label="Featured on Home hero"
          name="hero_featured"
          valuePropName="checked"
          tooltip="At most one event can be pinned — pinning this one automatically unpins any other. With nothing pinned, the app shows the latest event by start date."
        >
          <Checkbox>Pin this event as the main hero on the mobile Home tab</Checkbox>
        </Form.Item>

        <Divider orientation="left">Home Featured Carousel</Divider>
        <Form.Item label="Featured" name="is_featured" valuePropName="checked">
          <Checkbox>Show this event in the mobile Home featured-events carousel</Checkbox>
        </Form.Item>
        <Form.Item
          label="Featured Type"
          name="featured_type"
          tooltip="Announcement → tapping the card opens a full-screen image close-up. Event → tapping opens the event detail screen."
        >
          <Select
            options={[
              { label: 'Announcement (image close-up)', value: 'announcement' },
              { label: 'Event (opens detail screen)', value: 'event' },
            ]}
          />
        </Form.Item>
        <Form.Item
          noStyle
          shouldUpdate={(prev, cur) => prev.featured_type !== cur.featured_type}
        >
          {({ getFieldValue }) =>
            getFieldValue('featured_type') === 'event' ? (
              <Form.Item
                label="Linked Event Session"
                name="featured_session_id"
                tooltip="The session opened in the event detail screen when this featured EVENT card is tapped. Leave blank to open the Events tab."
              >
                <Select {...sessionSelectProps} placeholder="Select a session" allowClear />
              </Form.Item>
            ) : null
          }
        </Form.Item>
        <ImageUploadField
          form={form}
          name="image_url"
          label="Carousel Image URL"
          tooltip="Hero image on the featured card / the close-up shown for an announcement."
          currentUrl={currentImageUrl}
        />
      </Form>
    </Edit>
  )
}
